// NPM Imports
import typia from "typia";

// Local Imports
import { exhaust, Validator, Context } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import type { ContentKind as SchemaContentKind } from "GenAlphaSchema";
import type { DepotGet } from "Depot";
import { Schematic, Symbol } from "./schematic";
import { Layout } from "./layout";

export type ContentKind = "schematic" | "symbol" | "layout" | "graphics";
export const ContentKind = {
  Schematic: "schematic",
  Symbol: "symbol",
  Layout: "layout",
  Graphics: "graphics",
} as const;

export type GraphicsContent = schema.GraphicsContent;

// # Content Type Union
// The things that can be "primary content".
// Internally tagged by the `contentKind` field.
export type Content = Schematic | Symbol | Layout | GraphicsContent;

export const Content = {
  Kind: ContentKind,
  toSchema: (content: Content): schema.Content => {
    switch (content.contentKind) {
      case ContentKind.Schematic:
        return Schematic.toJson(content);
      case ContentKind.Symbol:
        return Symbol.toJson(content);
      case ContentKind.Layout:
        return content.toSchema();
      case ContentKind.Graphics:
        return content; // Unchanged
      default:
        exhaust(content);
    }
  },

  // FIXME: return Result
  fromSchema: async (
    ctx: Context,
    content: schema.Content,
    depot: DepotGet,
    namespace: string
  ): Promise<Content> => {
    switch (content.contentKind) {
      case "schematic": {
        const result = await Schematic.fromSchema(
          ctx,
          content,
          depot,
          namespace
        );
        if (result.err) {
          throw new Error(`Failed to convert schematic: ${result.val.why}`);
        }
        return result.val;
      }
      case "symbol":
        return Symbol.fromJson(content);
      case "layout": {
        const result = await Layout.fromSchema(ctx, content, depot, namespace);
        if (result.err) {
          throw new Error(
            `Failed to convert layout: ${JSON.stringify(result.val)}`
          );
        }
        return result.val;
      }
      case "graphics":
        return content;
      default:
        throw new Error(
          `Unsupported content kind: ${(content as any).contentKind}`
        );
    }
  },
};

// # Content JSON
export type ContentJson = schema.Content;

export const ContentJson = {
  Kind: ContentKind,
  validator: new Validator(typia.createValidate<ContentJson>()),
  stringify: typia.json.createStringify<ContentJson>(),
};

// Schema content kind constants
const SchemaContentKinds = {
  Layout: "layout",
  Schematic: "schematic",
  Symbol: "symbol",
  Graphics: "graphics",
} as const;

/**
 * Maps schema ContentKind to model ContentKind for conversion
 * @param schemaContentKind The content kind from the schema
 * @returns The corresponding model ContentKind
 */
export function schemaContentKindToModel(
  schemaContentKind: SchemaContentKind
): ContentKind {
  switch (schemaContentKind) {
    case SchemaContentKinds.Layout:
      return ContentKind.Layout;
    case SchemaContentKinds.Schematic:
      return ContentKind.Schematic;
    case SchemaContentKinds.Symbol:
      return ContentKind.Symbol;
    case SchemaContentKinds.Graphics:
      return ContentKind.Graphics;
    default:
      throw new Error(`Unknown schema content kind: ${schemaContentKind}`);
  }
}
